/**
 * CalendarResourceTypesPage
 *
 * CRUD page for managing Calendar Resource Types per agent.
 * Allows operators to define resource types with configurable booking modes,
 * advance booking limits, duration constraints, and cancellation policies.
 *
 * Validates: Requirements 6.1, 6.2, 6.3, 6.4, 6.5, 6.6
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete, TENANT_TOKEN_KEY } from '../../api/client';
import { Card, Badge, Button, Input, Modal } from '../../design-system';
import { tokens } from '../../design-system/tokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TimeBlock {
  name: string;
  startTime: string;
  endTime: string;
}

interface ModeConfig {
  blocks?: TimeBlock[];
  durationMinutes?: number;
  bufferMinutes?: number;
}

interface CancellationPolicy {
  allowCancellation: boolean;
  hoursBeforeDeadline: number;
}

interface ResourceType {
  id: string;
  virtualAgentId: string;
  name: string;
  bookingMode: 'daily' | 'time-block' | 'appointment';
  category?: 'general' | 'hoteleria';
  checkInTime?: string | null;
  checkOutTime?: string | null;
  modeConfig: ModeConfig | null;
  minAdvanceDays: number;
  maxAdvanceDays: number;
  minDuration: number | null;
  maxDuration: number | null;
  cancellationPolicy: CancellationPolicy | null;
  bookingStatusSettings?: unknown | null;
  createdAt: string;
  updatedAt: string;
  _count?: { resources: number };
}

interface FormData {
  name: string;
  category: 'general' | 'hoteleria';
  checkInTime: string;
  checkOutTime: string;
  bookingMode: 'daily' | 'time-block' | 'appointment';
  modeConfig: ModeConfig;
  minAdvanceDays: number;
  maxAdvanceDays: number;
  minDuration: number | null;
  maxDuration: number | null;
  cancellationPolicy: CancellationPolicy;
  bookingRestrictStatuses: boolean;
  bookingOpPending: boolean;
  bookingOpConfirmed: boolean;
  bookingOpHuman: boolean;
  bookingLabelPending: string;
  bookingLabelConfirmed: string;
  bookingLabelHuman: string;
}

const EMPTY_FORM: FormData = {
  name: '',
  category: 'general',
  checkInTime: '',
  checkOutTime: '',
  bookingMode: 'daily',
  modeConfig: {},
  minAdvanceDays: 0,
  maxAdvanceDays: 90,
  minDuration: null,
  maxDuration: null,
  cancellationPolicy: { allowCancellation: true, hoursBeforeDeadline: 24 },
  bookingRestrictStatuses: false,
  bookingOpPending: true,
  bookingOpConfirmed: true,
  bookingOpHuman: true,
  bookingLabelPending: '',
  bookingLabelConfirmed: '',
  bookingLabelHuman: '',
};

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconCalendar() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
      <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const BOOKING_MODE_LABELS: Record<string, string> = {
  daily: 'Día completo',
  'time-block': 'Bloque horario',
  appointment: 'Cita',
};

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  hoteleria: 'Hotelería',
};

const TIME_HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

function bookingModeBadgeVariant(mode: string): 'accent' | 'warning' | 'success' {
  switch (mode) {
    case 'daily': return 'accent';
    case 'time-block': return 'warning';
    case 'appointment': return 'success';
    default: return 'accent';
  }
}

function parseBookingStatusSettingsToFormFields(raw: unknown): Pick<
  FormData,
  | 'bookingRestrictStatuses'
  | 'bookingOpPending'
  | 'bookingOpConfirmed'
  | 'bookingOpHuman'
  | 'bookingLabelPending'
  | 'bookingLabelConfirmed'
  | 'bookingLabelHuman'
> {
  const base = {
    bookingRestrictStatuses: false,
    bookingOpPending: true,
    bookingOpConfirmed: true,
    bookingOpHuman: true,
    bookingLabelPending: '',
    bookingLabelConfirmed: '',
    bookingLabelHuman: '',
  };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const o = raw as { enabledStatuses?: unknown; labels?: unknown };
  const out = { ...base };
  if (Array.isArray(o.enabledStatuses) && o.enabledStatuses.length > 0) {
    out.bookingRestrictStatuses = true;
    out.bookingOpPending = o.enabledStatuses.includes('pending_deposit');
    out.bookingOpConfirmed = o.enabledStatuses.includes('confirmed');
    out.bookingOpHuman = o.enabledStatuses.includes('human_supervision');
  }
  if (o.labels && typeof o.labels === 'object' && !Array.isArray(o.labels)) {
    const L = o.labels as Record<string, unknown>;
    if (typeof L.pending_deposit === 'string') out.bookingLabelPending = L.pending_deposit;
    if (typeof L.confirmed === 'string') out.bookingLabelConfirmed = L.confirmed;
    if (typeof L.human_supervision === 'string') out.bookingLabelHuman = L.human_supervision;
  }
  return out;
}

function buildBookingStatusSettingsPayload(form: FormData): unknown | null {
  const labels: Record<string, string> = {};
  const t = (s: string) => s.trim();
  if (t(form.bookingLabelPending)) labels.pending_deposit = t(form.bookingLabelPending).slice(0, 120);
  if (t(form.bookingLabelConfirmed)) labels.confirmed = t(form.bookingLabelConfirmed).slice(0, 120);
  if (t(form.bookingLabelHuman)) labels.human_supervision = t(form.bookingLabelHuman).slice(0, 120);
  const hasLabels = Object.keys(labels).length > 0;

  if (form.bookingRestrictStatuses) {
    const enabledStatuses: string[] = [];
    if (form.bookingOpPending) enabledStatuses.push('pending_deposit');
    if (form.bookingOpConfirmed) enabledStatuses.push('confirmed');
    if (form.bookingOpHuman) enabledStatuses.push('human_supervision');
    const o: Record<string, unknown> = { enabledStatuses };
    if (hasLabels) o.labels = labels;
    return o;
  }
  if (hasLabels) return { labels };
  return null;
}

function resourceTypeHasBookingStatusConfig(rt: ResourceType): boolean {
  const s = rt.bookingStatusSettings;
  if (s == null || typeof s !== 'object' || Array.isArray(s)) return false;
  const o = s as { enabledStatuses?: unknown; labels?: unknown };
  if (Array.isArray(o.enabledStatuses) && o.enabledStatuses.length > 0) return true;
  if (o.labels && typeof o.labels === 'object' && !Array.isArray(o.labels) && Object.keys(o.labels as object).length > 0) {
    return true;
  }
  return false;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CalendarResourceTypesPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ─── Data Loading ────────────────────────────────────────────────────────

  const loadResourceTypes = useCallback(async () => {
    if (!agentId || !token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiGet<ResourceType[]>(
        `/api/agents/${agentId}/calendar/resource-types`,
        token
      );
      setResourceTypes(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [agentId, token]);

  useEffect(() => {
    void loadResourceTypes();
  }, [loadResourceTypes]);

  // ─── Form Handlers ───────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setModalOpen(true);
  };

  const openEditModal = (rt: ResourceType) => {
    setEditingId(rt.id);
    setForm({
      name: rt.name,
      category: rt.category === 'hoteleria' ? 'hoteleria' : 'general',
      checkInTime: rt.checkInTime ?? '',
      checkOutTime: rt.checkOutTime ?? '',
      bookingMode: rt.bookingMode,
      modeConfig: rt.modeConfig || {},
      minAdvanceDays: rt.minAdvanceDays,
      maxAdvanceDays: rt.maxAdvanceDays,
      minDuration: rt.minDuration,
      maxDuration: rt.maxDuration,
      cancellationPolicy: rt.cancellationPolicy || { allowCancellation: true, hoursBeforeDeadline: 24 },
      ...parseBookingStatusSettingsToFormFields(rt.bookingStatusSettings),
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) {
      errors.name = 'El nombre es requerido';
    }
    if (form.minAdvanceDays < 0) {
      errors.minAdvanceDays = 'Debe ser 0 o mayor';
    }
    if (form.maxAdvanceDays < 1) {
      errors.maxAdvanceDays = 'Debe ser al menos 1';
    }
    if (form.maxAdvanceDays <= form.minAdvanceDays) {
      errors.maxAdvanceDays = 'Debe ser mayor que el mínimo';
    }
    if (form.bookingMode === 'time-block') {
      const blocks = form.modeConfig.blocks || [];
      if (blocks.length === 0) {
        errors.blocks = 'Debe definir al menos un bloque horario';
      }
    }
    if (form.bookingMode === 'appointment') {
      if (!form.modeConfig.durationMinutes || form.modeConfig.durationMinutes < 1) {
        errors.durationMinutes = 'La duración debe ser al menos 1 minuto';
      }
    }
    if (form.category === 'hoteleria') {
      if (!TIME_HHMM.test(form.checkInTime)) {
        errors.checkInTime = 'Indicá el check-in en formato HH:mm';
      }
      if (!TIME_HHMM.test(form.checkOutTime)) {
        errors.checkOutTime = 'Indicá el check-out en formato HH:mm';
      }
    }
    if (form.bookingRestrictStatuses) {
      if (!form.bookingOpPending && !form.bookingOpConfirmed && !form.bookingOpHuman) {
        errors.bookingStatuses = 'Si restringe estados, marque al menos uno';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !agentId || !token) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        checkInTime: form.category === 'hoteleria' ? form.checkInTime : null,
        checkOutTime: form.category === 'hoteleria' ? form.checkOutTime : null,
        bookingMode: form.bookingMode,
        modeConfig: form.bookingMode === 'daily' ? null : form.modeConfig,
        minAdvanceDays: form.minAdvanceDays,
        maxAdvanceDays: form.maxAdvanceDays,
        minDuration: form.minDuration,
        maxDuration: form.maxDuration,
        cancellationPolicy: form.cancellationPolicy,
        bookingStatusSettings: buildBookingStatusSettingsPayload(form),
      };

      if (editingId) {
        await apiPut(
          `/api/agents/${agentId}/calendar/resource-types/${editingId}`,
          payload,
          token,
          { successMessage: 'Tipo de recurso actualizado' }
        );
      } else {
        await apiPost(
          `/api/agents/${agentId}/calendar/resource-types`,
          payload,
          token,
          { successMessage: 'Tipo de recurso creado' }
        );
      }
      closeModal();
      await loadResourceTypes();
    } catch (e) {
      setFormErrors({ _general: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId || !agentId || !token) return;
    setDeleting(true);
    try {
      await apiDelete(
        `/api/agents/${agentId}/calendar/resource-types/${deleteId}`,
        token,
        { successMessage: 'Tipo de recurso eliminado' }
      );
      setDeleteId(null);
      await loadResourceTypes();
    } catch (e) {
      setError((e as Error).message);
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Time Block Helpers ──────────────────────────────────────────────────

  const addTimeBlock = () => {
    const blocks = form.modeConfig.blocks || [];
    setForm({
      ...form,
      modeConfig: {
        ...form.modeConfig,
        blocks: [...blocks, { name: '', startTime: '09:00', endTime: '12:00' }],
      },
    });
  };

  const updateTimeBlock = (index: number, field: keyof TimeBlock, value: string) => {
    const blocks = [...(form.modeConfig.blocks || [])];
    blocks[index] = { ...blocks[index], [field]: value };
    setForm({ ...form, modeConfig: { ...form.modeConfig, blocks } });
  };

  const removeTimeBlock = (index: number) => {
    const blocks = [...(form.modeConfig.blocks || [])];
    blocks.splice(index, 1);
    setForm({ ...form, modeConfig: { ...form.modeConfig, blocks } });
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!token) return null;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/app/agents/${agentId}/plugins`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 12,
            transition: `color ${tokens.transitions.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
          onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
        >
          <IconArrowLeft />
          Volver a plugins
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: tokens.borderRadius.lg,
                backgroundColor: tokens.colors.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.colors.primary[600],
              }}
            >
              <IconCalendar />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>
                Tipos de Recurso
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[500] }}>
                Modos de reserva, cancelación y <strong style={{ color: tokens.colors.neutral[700] }}>estados de reserva por tipo</strong> (al crear o editar un tipo, sección «Estados de reserva»).
              </p>
            </div>
          </div>
          <Button variant="primary" size="md" onClick={openCreateModal}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconPlus /> Crear Tipo de Recurso
            </span>
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: tokens.colors.neutral[500] }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', marginRight: 12 }}>
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
          Cargando tipos de recurso...
        </div>
      )}

      {/* Empty state */}
      {!loading && resourceTypes.length === 0 && !error && (
        <Card variant="outlined" padding="lg">
          <div style={{ textAlign: 'center', color: tokens.colors.neutral[500], padding: 24 }}>
            <IconCalendar />
            <p style={{ marginTop: 12, marginBottom: 16 }}>No hay tipos de recurso configurados.</p>
            <Button variant="primary" size="sm" onClick={openCreateModal}>
              Crear primer tipo de recurso
            </Button>
          </div>
        </Card>
      )}

      {/* Resource Types List */}
      {!loading && resourceTypes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {resourceTypes.map((rt) => (
            <Card key={rt.id} variant="outlined" padding="md">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
                      {rt.name}
                    </h3>
                    <Badge variant={bookingModeBadgeVariant(rt.bookingMode)} size="sm">
                      {BOOKING_MODE_LABELS[rt.bookingMode] || rt.bookingMode}
                    </Badge>
                    <Badge variant={rt.category === 'hoteleria' ? 'accent' : 'neutral'} size="sm">
                      {CATEGORY_LABELS[rt.category ?? 'general'] ?? rt.category}
                    </Badge>
                    {resourceTypeHasBookingStatusConfig(rt) && (
                      <Badge variant="neutral" size="sm">
                        Estados reserva
                      </Badge>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 13, color: tokens.colors.neutral[500] }}>
                    <span>Anticipación: {rt.minAdvanceDays}–{rt.maxAdvanceDays} días</span>
                    {rt.category === 'hoteleria' && rt.checkInTime && rt.checkOutTime && (
                      <span>Check-in {rt.checkInTime} · Check-out {rt.checkOutTime}</span>
                    )}
                    {rt._count && <span>Recursos: {rt._count.resources}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(rt)}>
                    <IconEdit />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteId(rt.id)}>
                    <IconTrash />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Editar Tipo de Recurso' : 'Crear Tipo de Recurso'}
        size="lg"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={closeModal} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {formErrors._general && (
            <div style={{ padding: '8px 12px', borderRadius: tokens.borderRadius.md, backgroundColor: tokens.colors.error.light, color: tokens.colors.error.dark, fontSize: 13 }}>
              {formErrors._general}
            </div>
          )}

          {/* Name */}
          <Input
            label="Nombre"
            placeholder="Ej: Cabaña, Consultorio, Salón"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={formErrors.name}
            fullWidth
          />

          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Tipo
            </label>
            <select
              value={form.category}
              onChange={(e) => {
                const category = e.target.value as FormData['category'];
                setForm({
                  ...form,
                  category,
                  checkInTime: category === 'hoteleria' ? form.checkInTime : '',
                  checkOutTime: category === 'hoteleria' ? form.checkOutTime : '',
                });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.neutral[300]}`,
                backgroundColor: tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
                outline: 'none',
              }}
            >
              <option value="general">General</option>
              <option value="hoteleria">Hotelería</option>
            </select>
          </div>

          {form.category === 'hoteleria' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input
                label="Check-in"
                type="time"
                value={form.checkInTime}
                onChange={(e) => setForm({ ...form, checkInTime: e.target.value })}
                error={formErrors.checkInTime}
                fullWidth
              />
              <Input
                label="Check-out"
                type="time"
                value={form.checkOutTime}
                onChange={(e) => setForm({ ...form, checkOutTime: e.target.value })}
                error={formErrors.checkOutTime}
                fullWidth
              />
            </div>
          )}

          {/* Booking Mode */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Modo de Reserva
            </label>
            <select
              value={form.bookingMode}
              onChange={(e) => {
                const mode = e.target.value as FormData['bookingMode'];
                const newModeConfig: ModeConfig = mode === 'time-block'
                  ? { blocks: [{ name: 'Mañana', startTime: '09:00', endTime: '12:00' }] }
                  : mode === 'appointment'
                  ? { durationMinutes: 30, bufferMinutes: 10 }
                  : {};
                setForm({ ...form, bookingMode: mode, modeConfig: newModeConfig });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.neutral[300]}`,
                backgroundColor: tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
                outline: 'none',
              }}
            >
              <option value="daily">Día completo</option>
              <option value="time-block">Bloque horario</option>
              <option value="appointment">Cita (duración fija)</option>
            </select>
          </div>

          {/* Mode Config: Time Blocks */}
          {form.bookingMode === 'time-block' && (
            <div style={{ border: `1px solid ${tokens.colors.neutral[200]}`, borderRadius: tokens.borderRadius.md, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700] }}>
                  Bloques Horarios
                </label>
                <Button variant="ghost" size="sm" onClick={addTimeBlock}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IconPlus /> Agregar</span>
                </Button>
              </div>
              {formErrors.blocks && (
                <div style={{ fontSize: 13, color: tokens.colors.error.dark, marginBottom: 8 }}>{formErrors.blocks}</div>
              )}
              {(form.modeConfig.blocks || []).map((block, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <input
                    type="text"
                    placeholder="Nombre"
                    value={block.name}
                    onChange={(e) => updateTimeBlock(i, 'name', e.target.value)}
                    style={{ flex: 1, padding: '6px 10px', fontSize: 13, borderRadius: tokens.borderRadius.sm, border: `1px solid ${tokens.colors.neutral[300]}` }}
                  />
                  <input
                    type="time"
                    value={block.startTime}
                    onChange={(e) => updateTimeBlock(i, 'startTime', e.target.value)}
                    style={{ padding: '6px 8px', fontSize: 13, borderRadius: tokens.borderRadius.sm, border: `1px solid ${tokens.colors.neutral[300]}` }}
                  />
                  <span style={{ color: tokens.colors.neutral[400], fontSize: 13 }}>a</span>
                  <input
                    type="time"
                    value={block.endTime}
                    onChange={(e) => updateTimeBlock(i, 'endTime', e.target.value)}
                    style={{ padding: '6px 8px', fontSize: 13, borderRadius: tokens.borderRadius.sm, border: `1px solid ${tokens.colors.neutral[300]}` }}
                  />
                  <button
                    type="button"
                    onClick={() => removeTimeBlock(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: tokens.colors.error.main, padding: 4 }}
                    aria-label="Eliminar bloque"
                  >
                    <IconTrash />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Mode Config: Appointment */}
          {form.bookingMode === 'appointment' && (
            <div style={{ display: 'flex', gap: 12 }}>
              <Input
                label="Duración (minutos)"
                type="number"
                min={1}
                value={form.modeConfig.durationMinutes ?? ''}
                onChange={(e) => setForm({ ...form, modeConfig: { ...form.modeConfig, durationMinutes: parseInt(e.target.value) || 0 } })}
                error={formErrors.durationMinutes}
                fullWidth
              />
              <Input
                label="Buffer entre citas (min)"
                type="number"
                min={0}
                value={form.modeConfig.bufferMinutes ?? ''}
                onChange={(e) => setForm({ ...form, modeConfig: { ...form.modeConfig, bufferMinutes: parseInt(e.target.value) || 0 } })}
                fullWidth
              />
            </div>
          )}

          {/* Advance Days */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Input
              label="Anticipación mínima (días)"
              type="number"
              min={0}
              value={form.minAdvanceDays}
              onChange={(e) => setForm({ ...form, minAdvanceDays: parseInt(e.target.value) || 0 })}
              error={formErrors.minAdvanceDays}
              fullWidth
            />
            <Input
              label="Anticipación máxima (días)"
              type="number"
              min={1}
              value={form.maxAdvanceDays}
              onChange={(e) => setForm({ ...form, maxAdvanceDays: parseInt(e.target.value) || 0 })}
              error={formErrors.maxAdvanceDays}
              fullWidth
            />
          </div>

          {/* Duration Constraints */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Input
              label="Duración mínima"
              type="number"
              min={0}
              placeholder="Sin mínimo"
              value={form.minDuration ?? ''}
              onChange={(e) => setForm({ ...form, minDuration: e.target.value ? parseInt(e.target.value) : null })}
              helperText="En unidades según modo"
              fullWidth
            />
            <Input
              label="Duración máxima"
              type="number"
              min={0}
              placeholder="Sin máximo"
              value={form.maxDuration ?? ''}
              onChange={(e) => setForm({ ...form, maxDuration: e.target.value ? parseInt(e.target.value) : null })}
              helperText="En unidades según modo"
              fullWidth
            />
          </div>

          {/* Estados de reserva (por tipo de recurso) */}
          <div style={{ border: `1px solid ${tokens.colors.neutral[200]}`, borderRadius: tokens.borderRadius.md, padding: 12 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 8 }}>
              Estados de reserva
            </label>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: tokens.colors.neutral[500] }}>
              Aplica a todos los recursos de este tipo. Puede limitar qué estados elige la operadora al crear o editar reservas, y opcionalmente personalizar las etiquetas en pantalla.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <input
                type="checkbox"
                id="bookingRestrictStatuses"
                checked={form.bookingRestrictStatuses}
                onChange={(e) => setForm({ ...form, bookingRestrictStatuses: e.target.checked })}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="bookingRestrictStatuses" style={{ fontSize: 14, color: tokens.colors.neutral[700], cursor: 'pointer' }}>
                Restringir estados disponibles para la operadora
              </label>
            </div>
            {form.bookingRestrictStatuses && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.bookingOpPending}
                    onChange={(e) => setForm({ ...form, bookingOpPending: e.target.checked })}
                  />
                  Pendiente de seña
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.bookingOpConfirmed}
                    onChange={(e) => setForm({ ...form, bookingOpConfirmed: e.target.checked })}
                  />
                  Confirmada
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.bookingOpHuman}
                    onChange={(e) => setForm({ ...form, bookingOpHuman: e.target.checked })}
                  />
                  Supervisión humana
                </label>
              </div>
            )}
            {formErrors.bookingStatuses && (
              <div style={{ fontSize: 13, color: tokens.colors.error.dark, marginBottom: 8 }}>{formErrors.bookingStatuses}</div>
            )}
            <div style={{ fontSize: 13, fontWeight: 500, color: tokens.colors.neutral[600], marginBottom: 6 }}>
              Etiquetas personalizadas (opcional)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Input
                label="Pendiente de seña — texto en pantalla"
                placeholder="Ej.: Pendiente de seña"
                value={form.bookingLabelPending}
                onChange={(e) => setForm({ ...form, bookingLabelPending: e.target.value })}
                fullWidth
              />
              <Input
                label="Confirmada — texto en pantalla"
                placeholder="Ej.: Confirmada"
                value={form.bookingLabelConfirmed}
                onChange={(e) => setForm({ ...form, bookingLabelConfirmed: e.target.value })}
                fullWidth
              />
              <Input
                label="Supervisión humana — texto en pantalla"
                placeholder="Ej.: Revisión manual"
                value={form.bookingLabelHuman}
                onChange={(e) => setForm({ ...form, bookingLabelHuman: e.target.value })}
                fullWidth
              />
            </div>
          </div>

          {/* Cancellation Policy */}
          <div style={{ border: `1px solid ${tokens.colors.neutral[200]}`, borderRadius: tokens.borderRadius.md, padding: 12 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 8 }}>
              Política de Cancelación
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: form.cancellationPolicy.allowCancellation ? 12 : 0 }}>
              <input
                type="checkbox"
                id="allowCancellation"
                checked={form.cancellationPolicy.allowCancellation}
                onChange={(e) => setForm({
                  ...form,
                  cancellationPolicy: { ...form.cancellationPolicy, allowCancellation: e.target.checked },
                })}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="allowCancellation" style={{ fontSize: 14, color: tokens.colors.neutral[700], cursor: 'pointer' }}>
                Permitir cancelación
              </label>
            </div>
            {form.cancellationPolicy.allowCancellation && (
              <Input
                label="Horas antes del límite de cancelación"
                type="number"
                min={0}
                value={form.cancellationPolicy.hoursBeforeDeadline}
                onChange={(e) => setForm({
                  ...form,
                  cancellationPolicy: { ...form.cancellationPolicy, hoursBeforeDeadline: parseInt(e.target.value) || 0 },
                })}
                helperText="Horas antes de la reserva en que se puede cancelar"
                fullWidth
              />
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Eliminar Tipo de Recurso"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={() => setDeleteId(null)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: tokens.colors.neutral[700] }}>
          ¿Estás seguro de que deseas eliminar este tipo de recurso? Esta acción no se puede deshacer y eliminará todos los recursos asociados.
        </p>
      </Modal>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default CalendarResourceTypesPage;
